import { useEffect } from 'react';

export type UseScrollPaginationProps<T extends Element | null> = {
  targetRef: React.RefObject<T>;
  onLoadMore: () => Promise<void>;
  hasMore: boolean;
  isLoading: boolean;
  root?: Element | null;
  rootMargin?: string;
  threshold?: number;
  disabled?: boolean;
};

export function useScrollPagination<T extends Element | null>({
  targetRef,
  onLoadMore,
  hasMore,
  isLoading,
  root = null,
  rootMargin = '200px',
  threshold = 0.1,
  disabled = false,
}: UseScrollPaginationProps<T>): void {
  useEffect(() => {
    if (disabled || !targetRef?.current) return;

    const observerCallback: IntersectionObserverCallback = ([entry]) => {
      if (entry.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    };

    const observerOptions: IntersectionObserverInit = {
      root,
      rootMargin,
      threshold,
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const element = targetRef.current;
    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [targetRef, onLoadMore, hasMore, isLoading, root, rootMargin, threshold, disabled]);
}